import { useMemo } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { STATES } from '@/data/states';
import { BUSINESS_TYPES, OPPORTUNITY_TYPES } from '@/data/taxonomy';
import { useData } from '@/hooks/useData';
import { citiesOfState } from '@/lib/geo';

const ALL = '__all';

const FilterSelect = ({ id, label, value, onChange, options, placeholder, disabled }) => (
  <div className="min-w-0">
    <Label htmlFor={id} className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</Label>
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? '' : v)} disabled={disabled}>
      <SelectTrigger id={id} className="h-10 border-white/10 bg-[#0b0f16]" data-testid={id}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72 border-white/10 bg-[#0b0f16]">
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

/**
 * Filtros combináveis reutilizados em Home, Empresas e Oportunidades.
 * fields: lista de campos a exibir.
 */
export const ExploreFilters = ({ filters, onChange, onClear, fields = ['uf', 'city', 'categoryId', 'businessType', 'q'], resultCount, testPrefix = 'filters' }) => {
  const { categories, publicCompanies } = useData();
  const set = (k) => (v) => onChange({ ...filters, [k]: v, ...(k === 'uf' ? { city: '' } : {}) });

  const cityOptions = useMemo(() => {
    const set2 = new Set(filters.uf ? citiesOfState(filters.uf) : []);
    publicCompanies.forEach((c) => (!filters.uf || c.location.uf === filters.uf) && set2.add(c.location.city));
    return [...set2].sort((a, b) => a.localeCompare(b, 'pt-BR')).map((c) => ({ value: c, label: c }));
  }, [filters.uf, publicCompanies]);

  const active = Object.entries(filters).filter(([k, v]) => v && k !== 'sort').length;

  return (
    <div className="surface p-4 sm:p-5" data-testid={`${testPrefix}-panel`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <SlidersHorizontal className="h-4 w-4 text-primary" /> Filtros
          {active > 0 && <span className="rounded-md bg-primary/15 px-1.5 py-0.5 font-mono text-[11px] text-primary">{active} ativo{active > 1 ? 's' : ''}</span>}
        </div>
        <div className="flex items-center gap-3">
          {resultCount != null && <span className="font-mono text-xs text-muted-foreground" data-testid={`${testPrefix}-result-count`}>{resultCount} resultado{resultCount === 1 ? '' : 's'}</span>}
          <Button variant="ghost" size="sm" onClick={onClear} disabled={!active} data-testid={`${testPrefix}-clear-button`} className="text-muted-foreground">
            <X /> Limpar
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {fields.includes('q') && (
          <div className="sm:col-span-2 lg:col-span-1">
            <Label htmlFor={`${testPrefix}-keyword-input`} className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Palavra-chave</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id={`${testPrefix}-keyword-input`} value={filters.q || ''} onChange={(e) => set('q')(e.target.value)} placeholder="Ex.: usinagem, ERP..." className="h-10 border-white/10 bg-[#0b0f16] pl-9" data-testid={`${testPrefix}-keyword-input`} />
            </div>
          </div>
        )}
        {fields.includes('uf') && <FilterSelect id={`${testPrefix}-state-select`} label="Estado" value={filters.uf} onChange={set('uf')} placeholder="Todos os estados" options={STATES.map((s) => ({ value: s.uf, label: `${s.name} (${s.uf})` }))} />}
        {fields.includes('city') && <FilterSelect id={`${testPrefix}-city-select`} label="Cidade" value={filters.city} onChange={set('city')} placeholder={filters.uf ? 'Todas as cidades' : 'Todas as cidades'} options={cityOptions} />}
        {fields.includes('categoryId') && <FilterSelect id={`${testPrefix}-category-select`} label="Categoria" value={filters.categoryId} onChange={set('categoryId')} placeholder="Todas as categorias" options={categories.filter((c) => c.active).map((c) => ({ value: c.id, label: c.name }))} />}
        {fields.includes('businessType') && <FilterSelect id={`${testPrefix}-business-type-select`} label="Tipo de negócio" value={filters.businessType} onChange={set('businessType')} placeholder="Todos os tipos" options={BUSINESS_TYPES.map((b) => ({ value: b.id, label: b.name }))} />}
        {fields.includes('type') && <FilterSelect id={`${testPrefix}-opportunity-type-select`} label="Tipo de oportunidade" value={filters.type} onChange={set('type')} placeholder="Todos os tipos" options={OPPORTUNITY_TYPES.map((t) => ({ value: t.id, label: t.name }))} />}
        {fields.includes('period') && <FilterSelect id={`${testPrefix}-period-select`} label="Data de publicação" value={filters.period} onChange={set('period')} placeholder="Qualquer data" options={[{ value: '1', label: 'Últimas 24 horas' }, { value: '7', label: 'Últimos 7 dias' }, { value: '30', label: 'Últimos 30 dias' }, { value: '90', label: 'Últimos 90 dias' }]} />}
      </div>
    </div>
  );
};
